import React, { useState } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  useToast,
  Spinner,
  Text,
  Avatar,
  Badge,
  Flex,
} from '@chakra-ui/react';
import axios from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { auth } from '../../utils/firebaseConfig';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  isAccepted: boolean;
  isAdmin: boolean;
  headShotUrl?: string;
  createdAt?: Date | null;
}

function UsersManagement() {
  const token = useSelector((state: RootState) => state.authToken.value);
  const queryClient = useQueryClient();
  const toast = useToast();

  const fetchUsers = async () => {
    const authToken = await auth.currentUser?.getIdToken();
    const response = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}admin/users`,
      {
        headers: {
          Authorization: authToken,
          Accept: 'application/json',
        },
      }
    );
    return response.data;
  };

  const { data: users, isLoading, error } = useQuery<User[]>({
    queryKey: ['admin-users'],
    queryFn: fetchUsers,
  });

  const handleDeleteUser = async (userId: string, userName: string) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}?`)) {
      return;
    }

    try {
      const authToken = await auth.currentUser?.getIdToken();
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}users/remove/${userId}`,
        {
          headers: {
            Authorization: authToken,
            'Content-Type': 'application/json',
          },
        }
      );

      toast({
        title: 'User deleted',
        description: `${userName} has been deleted successfully.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });

      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    } catch (error) {
      console.error('Error deleting user:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete user.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" minH="200px">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Text color="red.500">Error loading users. Please try again.</Text>
      </Box>
    );
  }

  return (
    <Box overflowX="auto">
      <Table variant="simple" colorScheme="gray">
        <Thead>
          <Tr>
            <Th>Avatar</Th>
            <Th>Name</Th>
            <Th>Email</Th>
            <Th>Status</Th>
            <Th>Created</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {users && users.length > 0 ? (
            users.map((user) => (
              <Tr key={user.id}>
                <Td>
                  <Avatar size="sm" src={user.headShotUrl} name={`${user.firstName} ${user.lastName}`} />
                </Td>
                <Td>
                  {user.firstName} {user.lastName}
                  {user.isAdmin && (
                    <Badge ml={2} colorScheme="purple">
                      Admin
                    </Badge>
                  )}
                </Td>
                <Td>{user.emailAddress}</Td>
                <Td>
                  <Badge colorScheme={user.isAccepted ? 'green' : 'yellow'}>
                    {user.isAccepted ? 'Accepted' : 'Pending'}
                  </Badge>
                </Td>
                <Td>
                  {user.createdAt
                    ? new Date(user.createdAt).toLocaleDateString()
                    : 'N/A'}
                </Td>
                <Td>
                  <Button
                    colorScheme="red"
                    size="sm"
                    onClick={() =>
                      handleDeleteUser(
                        user.id,
                        `${user.firstName} ${user.lastName}`
                      )
                    }
                  >
                    Delete
                  </Button>
                </Td>
              </Tr>
            ))
          ) : (
            <Tr>
              <Td colSpan={6} textAlign="center">
                <Text>No users found</Text>
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
    </Box>
  );
}

export default UsersManagement;

